import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './SubmissionGuidePage.css';

const tabs = [
  { id: 'github', label: 'GitHub' },
  { id: 'gitlab', label: 'GitLab' },
];

const SubmissionGuidePage = () => {
  const [activeTab, setActiveTab] = useState('github');

  return (
    <div className="guide-page">
      <div className="guide-container">
        <Link to="/internship" className="guide-back-link">← Back to Dashboard</Link>

        <div className="guide-header">
          <span className="guide-eyebrow">Submission Guide</span>
          <h1 className="guide-title">How to Submit Your Project</h1>
          <p className="guide-subtitle">
            Follow the steps below to create a repository, push your code, and submit the link on CodeHub.
          </p>
        </div>

        {/* ── Overview ── */}
        <div className="guide-section">
          <h2>Overview</h2>
          <p>
            When you complete a project stage, you need to submit a <strong>public repository link</strong> (GitHub or GitLab)
            containing your code. Our review team will access your repository to evaluate your submission.
          </p>
          <div className="guide-callout guide-callout-info">
            <strong>💡 Why public?</strong> Your repository must be <strong>public</strong> so our reviewers can access and
            evaluate your code. A private repository will be rejected during review.
          </div>
        </div>

        {/* ── What You Need ── */}
        <div className="guide-section">
          <h2>What You Need</h2>
          <ul className="guide-checklist">
            <li>A free <strong>GitHub</strong> or <strong>GitLab</strong> account</li>
            <li><strong>Git</strong> installed on your computer (<a href="https://git-scm.com/downloads" target="_blank" rel="noopener noreferrer">Download Git</a>)</li>
            <li>Your completed project code on your local machine</li>
          </ul>
        </div>

        {/* ── Tab Selector ── */}
        <div className="guide-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`guide-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── GitHub Steps ── */}
        {activeTab === 'github' && (
          <div className="guide-section">
            <h2>Step-by-Step: GitHub</h2>

            <div className="guide-step">
              <span className="guide-step-num">1</span>
              <div>
                <h3>Create a GitHub Account</h3>
                <p>
                  Go to <a href="https://github.com/signup" target="_blank" rel="noopener noreferrer">github.com/signup</a> and
                  create a free account if you don't have one already.
                </p>
              </div>
            </div>

            <div className="guide-step">
              <span className="guide-step-num">2</span>
              <div>
                <h3>Create a New Repository</h3>
                <p>
                  Click the <strong>+</strong> icon in the top-right corner → <strong>New repository</strong>.
                </p>
                <ul>
                  <li>Give it a meaningful name (e.g. <code>codehub-notice-board</code>)</li>
                  <li>Set visibility to <strong>Public</strong></li>
                  <li>Check "Add a README file"</li>
                  <li>Click <strong>Create repository</strong></li>
                </ul>
              </div>
            </div>

            <div className="guide-step">
              <span className="guide-step-num">3</span>
              <div>
                <h3>Push Your Code</h3>
                <p>Open a terminal in your project folder and run:</p>
                <pre className="guide-code">
{`git init
git add .
git commit -m "Initial commit"
git branch -M main
git remote add origin https://github.com/YOUR_USERNAME/YOUR_REPO.git
git push -u origin main`}
                </pre>
                <p className="guide-note">Replace <code>YOUR_USERNAME</code> and <code>YOUR_REPO</code> with your actual GitHub username and repository name.</p>
              </div>
            </div>

            <div className="guide-step">
              <span className="guide-step-num">4</span>
              <div>
                <h3>Copy the Repository URL</h3>
                <p>
                  Go to your repository page on GitHub. Copy the URL from the browser address bar.
                  It should look like:
                </p>
                <pre className="guide-code">https://github.com/YOUR_USERNAME/YOUR_REPO</pre>
              </div>
            </div>

            <div className="guide-step">
              <span className="guide-step-num">5</span>
              <div>
                <h3>Submit on CodeHub</h3>
                <p>
                  Go to your project stage on CodeHub, paste the repository URL in the submission field,
                  and click <strong>Submit</strong>.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── GitLab Steps ── */}
        {activeTab === 'gitlab' && (
          <div className="guide-section">
            <h2>Step-by-Step: GitLab</h2>

            <div className="guide-step">
              <span className="guide-step-num">1</span>
              <div>
                <h3>Create a GitLab Account</h3>
                <p>
                  Go to <a href="https://gitlab.com/users/sign_up" target="_blank" rel="noopener noreferrer">gitlab.com/users/sign_up</a> and
                  create a free account if you don't have one already.
                </p>
              </div>
            </div>

            <div className="guide-step">
              <span className="guide-step-num">2</span>
              <div>
                <h3>Create a New Project</h3>
                <p>
                  Click <strong>New project</strong> → <strong>Create blank project</strong>.
                </p>
                <ul>
                  <li>Give it a meaningful name (e.g. <code>codehub-notice-board</code>)</li>
                  <li>Set visibility to <strong>Public</strong></li>
                  <li>Check "Initialize repository with a README"</li>
                  <li>Click <strong>Create project</strong></li>
                </ul>
              </div>
            </div>

            <div className="guide-step">
              <span className="guide-step-num">3</span>
              <div>
                <h3>Push Your Code</h3>
                <p>Open a terminal in your project folder and run:</p>
                <pre className="guide-code">
{`git init
git add .
git commit -m "Initial commit"
git branch -M main
git remote add origin https://gitlab.com/YOUR_USERNAME/YOUR_REPO.git
git push -u origin main`}
                </pre>
                <p className="guide-note">Replace <code>YOUR_USERNAME</code> and <code>YOUR_REPO</code> with your actual GitLab username and project name.</p>
              </div>
            </div>

            <div className="guide-step">
              <span className="guide-step-num">4</span>
              <div>
                <h3>Copy the Repository URL</h3>
                <p>
                  Go to your project page on GitLab. Copy the URL from the browser address bar.
                  It should look like:
                </p>
                <pre className="guide-code">https://gitlab.com/YOUR_USERNAME/YOUR_REPO</pre>
              </div>
            </div>

            <div className="guide-step">
              <span className="guide-step-num">5</span>
              <div>
                <h3>Submit on CodeHub</h3>
                <p>
                  Go to your project stage on CodeHub, paste the repository URL in the submission field,
                  and click <strong>Submit</strong>.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Important Notes ── */}
        <div className="guide-section">
          <h2>Important Notes</h2>
          <div className="guide-callout guide-callout-warn">
            <ul>
              <li>Your repository <strong>must be public</strong>. Private repositories cannot be reviewed.</li>
              <li>Do <strong>not</strong> delete or rename your repository after submitting — our team needs access during review.</li>
              <li>Each stage should be submitted in the <strong>same repository</strong>. Use folders or branches to organize stages if needed.</li>
              <li>Make sure your code is <strong>clean, well-structured</strong>, and includes a <strong>README.md</strong> explaining how to run the project.</li>
              <li>Plagiarized or copied code will be rejected.</li>
            </ul>
          </div>
        </div>

        {/* ── Troubleshooting ── */}
        <div className="guide-section">
          <h2>Common Issues</h2>

          <div className="guide-faq">
            <h3>"Permission denied" when pushing code?</h3>
            <p>
              You may need to set up SSH keys or use a personal access token. See{' '}
              <a href="https://docs.github.com/en/authentication" target="_blank" rel="noopener noreferrer">GitHub Auth Docs</a> or{' '}
              <a href="https://docs.gitlab.com/ee/user/ssh.html" target="_blank" rel="noopener noreferrer">GitLab SSH Docs</a>.
            </p>
          </div>

          <div className="guide-faq">
            <h3>"Repository not found" error on submission?</h3>
            <p>
              Make sure the repository is set to <strong>Public</strong> and the URL is correct.
              Try opening the URL in an incognito/private browser window to verify.
            </p>
          </div>

          <div className="guide-faq">
            <h3>Don't have Git installed?</h3>
            <p>
              Download and install Git from <a href="https://git-scm.com/downloads" target="_blank" rel="noopener noreferrer">git-scm.com</a>.
              After installing, restart your terminal and run <code>git --version</code> to verify.
            </p>
          </div>
        </div>

        {/* ── Need Help ── */}
        <div className="guide-section guide-help-box">
          <h2>Need Help?</h2>
          <p>
            If you're stuck at any step, use the <strong>Support</strong> section on your internship dashboard
            or email us at <a href="mailto:support@batosit.com">support@batosit.com</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubmissionGuidePage;
